/**
 * ETag Manager
 * Handles optimistic concurrency control via ETags
 *
 * ETags are used for:
 * - PATCH requests: Include If-Match header to prevent conflicts
 * - DELETE requests: Include If-Match header to prevent accidental deletions
 * - GET responses: Store ETag for later use in mutations
 *
 * On 412 Precondition Failed:
 * - React Query automatically refetches the resource
 * - User can retry with latest ETag
 */

const etags = new Map<string, string>()

/** Cache key format: "METHOD /path/to/resource". */
export function etagKey(method: string, url: string): string {
  return `${method.toUpperCase()} ${url}`
}

export function rememberEtag(key: string, etag: string | null) {
  if (etag) {
    etags.set(key, etag)
  }
}

export function getEtag(key: string): string | undefined {
  return etags.get(key)
}

/**
 * Derive a well-formed If-Match value from an object row.
 *
 * List rows carry `version` but no per-item ETag header, and `transformObject`
 * stores that bare version string in `_etag` — the server's `parseIfMatch`
 * only accepts the quoted forms (`W/"3"` / `"3"`), so a bare value is silently
 * ignored (no concurrency check at all). Wrap bare digits in the server's weak
 * format; pass already-quoted etags through untouched. Returns undefined when
 * neither `_etag` nor `version` is present (caller PATCHes unguarded, as before).
 */
export function ifMatchFromObject(
  obj: { _etag?: string; version?: number | string } | null | undefined,
): string | undefined {
  const raw = obj?._etag ?? (obj?.version != null ? String(obj.version) : undefined)
  if (!raw) return undefined
  return raw.includes('"') ? raw : `W/"${raw}"`
}

